import * as React from 'react';
import clsx from 'clsx';
import { useToast } from '@/context/ToastContext';
import { NotificationPanel } from './NotificationPanel';
import bellIconWhite from '@/assets/images/svg/bell_icon.svg';
import bellIconBlack from '@/assets/images/svg/bell_black_icon.svg';

interface NotificationButtonProps {
  /**
   * 'light' = White icon (for dark backgrounds)
   * 'dark' = Black icon (for light backgrounds)
   */
  theme?: 'light' | 'dark';
  className?: string;
}

/** Bell-icon header button that opens the `NotificationPanel`; theme picks light vs dark icon. */
export const NotificationButton: React.FC<NotificationButtonProps> = ({
  theme = 'light',
  className,
}) => {
  const { notificationHistory } = useToast();
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);

  const iconSrc = theme === 'dark' ? bellIconBlack : bellIconWhite;
  // Badge border should contrast with the icon/background
  const badgeBorder = theme === 'dark' ? 'border-white' : 'border-brand-800';

  // Calculate unread count
  const unreadCount = React.useMemo(
    () => notificationHistory.filter((notif) => !notif.read).length,
    [notificationHistory]
  );

  const handleClick = () => {
    setIsPanelOpen((prev) => !prev);
  };

  return (
    <>
      <button
        type='button'
        onClick={handleClick}
        className={clsx(
          'flex items-center justify-center p-2 transition-opacity hover:opacity-80 active:scale-95',
          className
        )}
        aria-label='Notifications'
      >
        {/* Wrapper: relative positioning context for the badge; Size: 24x24 (size-6) */}
        <div className='relative size-6'>
          <img src={iconSrc} alt='Notifications' className='size-full object-contain' />
          {unreadCount > 0 && (
            <span
              className={clsx(
                'absolute rounded-full border',
                // Specific requirements: top: 3px, left: 15px, relative to the 24px icon
                'left-[15px] top-[3px]',
                'size-2', // 8x8 pixels
                'bg-red-550', // #F44C4C
                badgeBorder
              )}
            />
          )}
        </div>
      </button>
      <NotificationPanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} />
    </>
  );
};
